import { dateOnly, MONEY_COLOR, overdueDays, validDate } from './util'
import type { RenderLine, RenderNode } from './util'

// 外部节点无数字主键：由业务编号派生稳定 id（仅作 React key / 点位 key，不回传后端）
export const stableID = (key: string): number => {
  let hash = 0
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0
  return hash
}
const externalNode = (patch: Partial<RenderNode> & Pick<RenderNode, 'id' | 'nodeLabel'>): RenderNode => ({
  date: null, status: 0, isDraw: false, isDds: false, isOutDate: false, overdueDays: 0, links: [], nodeFiles: [], ...patch,
})

export interface PaymentGroup { contractCode: string; contractName: string; nodes: API.ControlNodesZb.ContractNode[] }
export interface ExpenseGroup { purContractCode: string; purContractName: string; nodes: API.ControlNodesZb.ExpenseNode[] }

// 拷贝自老页面收款线分组：按 salesContractCode 分组、组内按日期升序
export const paymentGroups = (items: API.ControlNodesZb.ContractNode[]): PaymentGroup[] => {
  const map = new Map<string, PaymentGroup>()
  items.forEach(item => {
    if (!item.salesContractCode) return
    const group = map.get(item.salesContractCode) || { contractCode: item.salesContractCode, contractName: item.salesContractName || '', nodes: [] }
    group.nodes.push(item)
    map.set(item.salesContractCode, group)
  })
  map.forEach(group => group.nodes.sort((a, b) => (dateOnly(a.date) || '').localeCompare(dateOnly(b.date) || '')))
  return [...map.values()]
}
export const paymentLines = (items: API.ControlNodesZb.ContractNode[]): RenderLine[] => paymentGroups(items).map(group => ({
  lineID: `zb-payment-${group.contractCode}`, lineName: `收款-${group.contractName}`, colorValue: MONEY_COLOR, nodeType: 'payment', externalCode: group.contractCode,
  nodes: group.nodes.map(n => externalNode({
    id: stableID(`payment:${group.contractCode}:${n.pmCode}`), nodeLabel: n.description || '', date: dateOnly(n.date),
    status: n.status === '已收款' ? 1 : 0, moneyStatus: n.status, external: { pmCode: n.pmCode, contractName: group.contractName },
  })),
}))

// 拷贝自老页面费用线分组：按 purContractCode 分组，日期取 predictpaytime
export const expenseGroups = (items: API.ControlNodesZb.ExpenseNode[]): ExpenseGroup[] => {
  const map = new Map<string, ExpenseGroup>()
  items.forEach(item => {
    const group = map.get(item.purContractCode) || { purContractCode: item.purContractCode, purContractName: item.purContractName, nodes: [] }
    group.nodes.push(item)
    map.set(item.purContractCode, group)
  })
  return [...map.values()]
}
export const expenseLines = (items: API.ControlNodesZb.ExpenseNode[], receipts: API.ControlNodesZb.PaymentReceipt[] = [], payments: API.ControlNodesZb.ContractNode[] = []): RenderLine[] => expenseGroups(items).map(group => ({
  lineID: `zb-expense-${group.purContractCode}`, lineName: `费用-${group.purContractName}`, colorValue: '#333333', nodeType: 'expense', externalCode: group.purContractCode,
  nodes: group.nodes.map(n => externalNode({
    id: stableID(`expense:${group.purContractCode}:${n.paymentplancode}`), nodeLabel: n.paymentcondition || '', date: dateOnly(n.predictpaytime),
    // 老页面费用节点恒 isCompleted=true（已完成段画满），此处以 status=1 等价保留
    status: 1, moneyStatus: n.status,
    external: {
      contractName: group.purContractName, supplierName: n.supplierName, purchasematter: n.purchasematter,
      paymentcondition: n.paymentcondition, paymentplancode: n.paymentplancode,
      links: receipts.filter(r => r.fromContractCode === group.purContractCode && r.fromCode === n.paymentplancode).map(r => {
        const target = payments.find(p => p.salesContractCode === r.toContractCode && p.pmCode === r.toCode)
        const line = payments.find(p => p.salesContractCode === r.toContractCode)
        return { toContractCode: r.toContractCode, toCode: r.toCode, label: target && line ? `收款-${line.salesContractName}-${target.description}` : '' }
      }),
    },
  })),
}))

// 拷贝自老页面外协交圈线：每项按 *Time 字段展开为节点，无数据时不生成线
export const specialLine = (items: API.ControlNodesZb.SpecialLineItem[]): RenderLine | null => {
  if (!items.length) return null
  let link = ''
  const nodes: RenderNode[] = []
  items.forEach(item => {
    const prefix = `${item.xtProjectcode}-${item.xtProjectname}${item.majorName ? `-${item.majorName}` : ''}`
    Object.keys(item).forEach(key => {
      const value = item[key]
      if (key.endsWith('Time') && value) {
        const name = `${prefix}${item[`${key.replace(/Time$/, '')}TimeName`] || ''}`
        nodes.push(externalNode({ id: stableID(`special:${name}:${value}`), nodeLabel: name, date: dateOnly(value) }))
      }
    })
    if (item.subsidiaryCircleLink) link = item.subsidiaryCircleLink
  })
  return { lineID: 'zb-special', lineName: '外协交圈', colorValue: '#333333', nodeType: 'special', specialLink: link, nodes }
}

export const milestoneLine = (items: API.MilestoneZb.Item[], today?: string): RenderLine => ({
  lineID: 'zb-milestone', lineName: '总包设计控制线', colorValue: '#047857', nodeType: 'zbMilestone',
  nodes: items.map(m => {
    const date = m.status === 1 && validDate(m.actualDate) ? m.actualDate : validDate(m.planDate) ? m.planDate : null
    const overdue = overdueDays(m.planDate, m.status, today)
    return { id: m.id, nodeLabel: m.name, date, status: m.status === 1 ? 1 : 0, isDraw: false, isDds: false, isOutDate: overdue > 0, overdueDays: overdue, links: [], nodeFiles: [], milestone: m }
  }),
})
export const customLine = (line: API.ControlNodesZb.Line, today?: string): RenderLine => ({
  lineID: line.id, lineName: line.lineName, colorValue: line.colorValue, nodeType: 'default', assignName: line.assignUser?.name,
  nodes: [...line.nodes].sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999') || a.id - b.id).map(n => {
    const overdue = overdueDays(n.date, n.status, today)
    return { ...n, nodeLabel: n.nodeName, isOutDate: overdue > 0, overdueDays: overdue }
  }),
})
